<template>
  <nav class="tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-3">
    <div class="tw-w-32 tw-flex-none">
      <div v-if="!shouldHidePreviousCta" class="tw-cursor-pointer tw-rounded-md">
        <div class="tw-flex-0 tw-flex tw-rounded-md tw-border">
          <div
            data-testid="first-page-cta"
            class="tw-block tw-items-center tw-border-r tw-p-2 tw-text-xs tw-font-medium tw-text-slate-500"
            @click="goToFirstPage"
          >
            <ChevronDoubleLeft class="tw-h-4 tw-w-4" />
          </div>
          <div
            id="previous-button"
            class="tw-inline-flex tw-w-24 tw-items-center tw-justify-center tw-p-2 tw-pr-3 tw-text-xs tw-font-medium tw-text-slate-500 hover:tw-border-slate-300"
            @click="onClickPrevious"
          >
            Previous
          </div>
        </div>
      </div>
    </div>

    <div class="tw-flex-1 tw-text-center md:-tw-mt-px md:tw-flex">
      <div class="tw-mx-auto tw-text-xs tw-text-slate-400" v-html="text"></div>
    </div>

    <div class="tw-flex-0 tw-w-32">
      <div v-if="!shouldHideNextCta" class="tw-cursor-pointer tw-rounded-md">
        <div class="tw-flex-0 tw-flex tw-rounded-md tw-border">
          <div
            id="next-button"
            class="tw-inline-flex tw-w-24 tw-items-center tw-justify-center tw-p-2 tw-pl-3 tw-text-xs tw-font-medium tw-text-slate-500 hover:tw-border-slate-300"
            @click="onClickNext"
          >
            Next
          </div>
          <div
            data-testid="last-page-cta"
            class="tw-block tw-items-center tw-border-l tw-p-2 tw-text-xs tw-font-medium tw-text-slate-500"
            @click="goToLastPage"
          >
            <ChevronDoubleRight class="tw-h-4 tw-w-4" />
          </div>
        </div>
      </div>
    </div>
  </nav>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

import { ChevronDoubleLeft, ChevronDoubleRight } from './Icons';
import { formatNumber } from '../utils/numbers';

export default defineComponent({
  name: 'StudioPagination',
  components: {
    ChevronDoubleLeft,
    ChevronDoubleRight
  },
  props: {
    itemsCount: {
      type: Number,
      required: true
    },
    itemsPerPageCount: {
      type: Number,
      required: true
    },
    currentPage: {
      type: Number,
      required: true
    },
    suffix: {
      type: String,
      default: ''
    }
  },
  computed: {
    text(): string {
      if (this.currentPage === 1) {
        return `1 &ndash; ${formatNumber(this.itemsPerPageCount)} of ${formatNumber(this.itemsCount)}${
          this.suffix ? ` ${this.suffix}` : ''
        }`;
      }
      if (this.shouldHideNextCta) {
        const end = this.itemsPerPageCount * this.currentPage;
        const start = end - this.itemsPerPageCount;
        return `${formatNumber(start)} &ndash; ${formatNumber(this.itemsCount)} of ${formatNumber(this.itemsCount)}${
          this.suffix ? ` ${this.suffix}` : ''
        }`;
      }
      if (this.currentPage > 1) {
        const end = this.itemsPerPageCount * this.currentPage;
        const start = end - this.itemsPerPageCount;
        return `${formatNumber(start)} &ndash; ${formatNumber(end)} of ${formatNumber(this.itemsCount)}${
          this.suffix ? ` ${this.suffix}` : ''
        }`;
      }
      return '';
    },
    shouldHidePreviousCta(): boolean {
      return this.currentPage === 1;
    },
    shouldHideNextCta(): boolean {
      const numberOfPages = Math.ceil(this.itemsCount / this.itemsPerPageCount);
      return this.currentPage === numberOfPages;
    }
  },
  methods: {
    onClickNext() {
      this.$emit('next');
    },
    onClickPrevious() {
      this.$emit('previous');
    },
    goToFirstPage() {
      this.$emit('goToFirstPage');
    },
    goToLastPage() {
      const lastPage = Math.ceil(this.itemsCount / this.itemsPerPageCount);
      this.$emit('goToLastPage', lastPage);
    }
  }
});
</script>
